// /app/frontend/src/pages/risk/TIAWorkflowPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import {
  ChevronLeft, ChevronRight, Save, CheckCircle2, Globe, Shield,
  Lock, AlertTriangle, ClipboardCheck, UserCheck, Loader2, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { tiaAPI } from '../../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, title: 'Assessment Header', icon: FileText,      description: 'Reference, assessor and dates' },
  { id: 1, title: '1. Transfer Overview',       icon: Globe,         description: 'What data is being transferred and why' },
  { id: 2, title: '2. Country Assessment',      icon: AlertTriangle, description: 'Destination country legal landscape & risks' },
  { id: 3, title: '3. Supplementary Measures',  icon: Lock,          description: 'Technical, organisational and contractual safeguards' },
  { id: 4, title: '4. Risk Summary',            icon: Shield,        description: 'Inherent risk, effectiveness, residual risk' },
  { id: 5, title: '5. Transfer Decision',       icon: ClipboardCheck,description: 'Approve, suspend, or reject the transfer' },
  { id: 6, title: '6. Sign-Off',                icon: UserCheck,     description: 'Assessor, data owner and senior management sign-off' },
];

const RISK_LEVELS  = ['Low', 'Medium', 'High'];
const RISK_LEVELS_EXT = ['Low', 'Medium', 'High', 'Very High'];
const MEASURE_STATUSES = ['In Place', 'Planned', 'N/A'];
const EFFECTIVENESS_OPTS = ['Fully Effective', 'Partially Effective', 'Insufficient'];
const DECISION_OPTS = ['Approve', 'Approve with Conditions', 'Suspend', 'Reject'];
const TRANSFER_MECHANISMS = ['SCCs', 'BCRs', 'Adequacy Decision', 'Derogation', 'Other'];
const FREQUENCY_OPTS = ['One-off', 'Periodic', 'Continuous'];

const DECISION_COLORS = {
  'Approve':                  'border-emerald-500 bg-emerald-500/10 text-emerald-400',
  'Approve with Conditions':  'border-amber-500  bg-amber-500/10  text-amber-400',
  'Suspend':                  'border-orange-500 bg-orange-500/10 text-orange-400',
  'Reject':                   'border-rose-500   bg-rose-500/10   text-rose-400',
};

const STATUS_COLOR = {
  draft:         'bg-slate-500/10  text-slate-400  border-slate-500/20',
  in_progress:   'bg-blue-500/10   text-blue-400   border-blue-500/20',
  under_review:  'bg-amber-500/10  text-amber-400  border-amber-500/20',
  approved:      'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  rejected:      'bg-rose-500/10   text-rose-400   border-rose-500/20',
  suspended:     'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const RISK_FACTORS = [
  { key: 'surveillance',       label: 'Government access to personal data (surveillance laws)',
    guidance: 'Describe any laws enabling bulk / targeted surveillance' },
  { key: 'onward_transfer',    label: 'Onward transfers to third countries',
    guidance: 'Are onward transfers restricted or unregulated?' },
  { key: 'data_subject_rights',label: 'Effectiveness of data subject rights',
    guidance: 'Can individuals exercise access, erasure, objection?' },
  { key: 'judicial_redress',   label: 'Judicial redress and remedies',
    guidance: 'Are legal remedies available and effective?' },
  { key: 'enforcement',        label: 'Enforcement track record',
    guidance: 'Has the authority taken meaningful enforcement action?' },
  { key: 'sector_specific',    label: 'Sector-specific risks',
    guidance: 'Any industry regulations affecting this transfer?' },
];

const MEASURES = [
  { key: 'encryption_transit',  label: 'Encryption in transit',            placeholder: 'e.g., TLS 1.2+ for all data transfers' },
  { key: 'encryption_rest',     label: 'Encryption at rest',               placeholder: 'e.g., AES-256 encryption of stored data' },
  { key: 'pseudonymisation',    label: 'Pseudonymisation / Anonymisation',  placeholder: 'e.g., Data pseudonymised before transfer' },
  { key: 'access_controls',     label: 'Access controls',                  placeholder: 'e.g., RBAC, MFA, least privilege principles' },
  { key: 'contractual',         label: 'Contractual protections',          placeholder: 'e.g., SCCs with additional clauses, audit rights' },
  { key: 'data_minimisation',   label: 'Data minimisation',                placeholder: 'e.g., Only necessary data fields are transferred' },
  { key: 'transparency',        label: 'Transparency obligations',         placeholder: 'e.g., Importer will notify of government access requests' },
  { key: 'audit',               label: 'Audit & monitoring',               placeholder: 'e.g., Annual audits of importer\'s practices' },
  { key: 'staff_training',      label: 'Staff training',                   placeholder: 'e.g., Importer staff trained on data protection' },
  { key: 'incident_response',   label: 'Incident response',                placeholder: 'e.g., 72-hour breach notification obligation' },
];

const EMPTY_FORM = {
  status: 'draft', organisation: '', date_of_assessment: '', assessor_dpo: '', review_date: '',
  // S1
  transfer_description: '', data_exporter: '', data_importer: '', transfer_mechanism: '',
  categories_data_subjects: '', categories_personal_data: '', special_category_data: '',
  special_category_detail: '', purpose_of_transfer: '', frequency: '', retention_period: '',
  // S2
  destination_country: '', adequacy_decision: '', adequacy_decision_ref: '',
  data_protection_law: '', supervisory_authority: '', rule_of_law_index: '',
  risk_surveillance_assessment: '', risk_surveillance_level: '',
  risk_onward_transfer_assessment: '', risk_onward_transfer_level: '',
  risk_data_subject_rights_assessment: '', risk_data_subject_rights_level: '',
  risk_judicial_redress_assessment: '', risk_judicial_redress_level: '',
  risk_enforcement_assessment: '', risk_enforcement_level: '',
  risk_sector_specific_assessment: '', risk_sector_specific_level: '',
  overall_country_risk: '',
  // S3
  measure_encryption_transit_desc: 'TLS 1.2+ for all data transfers', measure_encryption_transit_status: '',
  measure_encryption_rest_desc: 'AES-256 encryption of stored data', measure_encryption_rest_status: '',
  measure_pseudonymisation_desc: 'Data pseudonymised before transfer', measure_pseudonymisation_status: '',
  measure_access_controls_desc: 'RBAC, MFA, least privilege principles', measure_access_controls_status: '',
  measure_contractual_desc: 'SCCs with additional clauses, audit rights', measure_contractual_status: '',
  measure_data_minimisation_desc: 'Only necessary data fields are transferred', measure_data_minimisation_status: '',
  measure_transparency_desc: 'Importer will notify of government access requests', measure_transparency_status: '',
  measure_audit_desc: "Annual audits of importer's practices", measure_audit_status: '',
  measure_staff_training_desc: 'Importer staff trained on data protection', measure_staff_training_status: '',
  measure_incident_response_desc: '72-hour breach notification obligation', measure_incident_response_status: '',
  // S4
  inherent_risk: '', measures_effectiveness: '', residual_risk: '', narrative_assessment: '',
  // S5
  decision: '', decision_conditions: '', next_review_date: '',
  // S6
  signoff_assessor_name: '', signoff_assessor_date: '',
  signoff_data_owner_name: '', signoff_data_owner_date: '',
  signoff_approved_by_name: '', signoff_approved_by_date: '',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function Field({ label, guidance, field, value, onChange, rows = 3 }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-200">{label}</Label>
      {guidance && <p className="text-xs text-slate-500 italic">{guidance}</p>}
      <Textarea
        value={value || ''}
        onChange={e => onChange(field, e.target.value)}
        placeholder="Enter your response..."
        rows={rows}
        className="bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-600 resize-none focus:border-violet-500"
      />
    </div>
  );
}

function InlineSelect({ label, field, value, onChange, options, placeholder = 'Select...' }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-200">{label}</Label>
      <Select value={value || ''} onValueChange={v => onChange(field, v)}>
        <SelectTrigger className="bg-slate-900 border-slate-700">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function RiskBadge({ level }) {
  if (!level) return null;
  const cls = {
    Low:       'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Medium:    'bg-amber-500/10   text-amber-400   border-amber-500/20',
    High:      'bg-rose-500/10    text-rose-400    border-rose-500/20',
    'Very High': 'bg-red-600/20   text-red-400     border-red-500/30',
  }[level] || '';
  return <Badge variant="outline" className={cls}>{level}</Badge>;
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function TIAWorkflowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isComplianceReadOnly: isReadOnly } = useAuth();
  const isNew = !id;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [tiaRef, setTiaRef] = useState('');

  useEffect(() => {
    if (!isNew) {
      tiaAPI.getOne(id)
        .then(r => { setForm({ ...EMPTY_FORM, ...r.data }); setTiaRef(r.data.tia_reference || ''); })
        .catch(() => { toast.error('TIA not found'); navigate('/risk/assessments'); })
        .finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const handleChange = useCallback((field, value) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  }, [isReadOnly]);

  const handleSave = useCallback(async (finalStatus) => {
    setSaving(true);
    try {
      const payload = { ...form, status: finalStatus || form.status };
      let result;
      if (isNew) {
        result = await tiaAPI.create(payload);
        setTiaRef(result.data.tia_reference);
        toast.success(`TIA ${result.data.tia_reference} created`);
        navigate(`/risk/tia/${result.data.id}`, { replace: true });
      } else {
        result = await tiaAPI.update(id, payload);
        toast.success('TIA saved');
      }
      setForm(prev => ({ ...prev, ...result.data }));
    } catch {
      toast.error('Failed to save TIA');
    } finally {
      setSaving(false);
    }
  }, [form, id, isNew, navigate]);

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="h-8 w-8 text-violet-400 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/risk/assessments')} className="text-slate-400">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-white">Transfer Impact Assessment</h1>
                {tiaRef && <Badge variant="outline" className="text-slate-400 border-slate-600">{tiaRef}</Badge>}
              </div>
              <p className="text-sm text-slate-400 mt-0.5">Evaluate the impact of international data transfers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn('capitalize', STATUS_COLOR[form.status])}>
              {form.status?.replace('_', ' ')}
            </Badge>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={() => handleSave()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            )}
          </div>
        </div>

        {/* ── Progress ── */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-slate-400">
            <span>{STEPS[step].title}</span>
            <span>Step {step + 1} of {STEPS.length}</span>
          </div>
          <Progress value={progress} className="h-1.5 bg-slate-800" />
        </div>

        {/* ── Step Tabs ── */}
        <div className="flex gap-1 overflow-x-auto pb-2">
          {STEPS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all",
                  step === s.id
                    ? "bg-violet-600 text-white"
                    : "bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-white"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.id === 0 ? 'Header' : `Sec ${s.id}`}
              </button>
            );
          })}
        </div>

        {/* ── Step Content ── */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <div className="flex items-center gap-3">
              {React.createElement(STEPS[step].icon, { className: "h-5 w-5 text-violet-400" })}
              <div>
                <CardTitle className="text-white">{STEPS[step].title}</CardTitle>
                <CardDescription>{STEPS[step].description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">

            {/* ── Step 0: Header ── */}
            {step === 0 && (
              <>
                <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-sm text-blue-300 font-medium mb-1">About Transfer Impact Assessments</p>
                  <p className="text-xs text-slate-400">
                    A TIA is required when transferring personal data to a third country without an adequacy decision.
                    It assesses whether the destination country provides an equivalent level of protection and
                    identifies supplementary measures required to ensure compliance with data protection law.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-slate-200">Organisation <span className="text-rose-400">*</span></Label>
                    <Input
                      value={form.organisation}
                      onChange={e => handleChange('organisation', e.target.value)}
                      placeholder="Enter organisation name"
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Assessment Reference</Label>
                    <Input
                      value={tiaRef || 'Auto-generated on save'}
                      disabled
                      className="bg-slate-900/50 border-slate-700 text-slate-500"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Status</Label>
                    <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          { v: 'draft', l: 'Draft' }, { v: 'in_progress', l: 'In Progress' },
                          { v: 'under_review', l: 'Under Review' }, { v: 'approved', l: 'Approved' },
                          { v: 'suspended', l: 'Suspended' }, { v: 'rejected', l: 'Rejected' },
                        ].map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Date of Assessment</Label>
                    <Input type="date" value={form.date_of_assessment} onChange={e => handleChange('date_of_assessment', e.target.value)} className="bg-slate-900 border-slate-700" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Review Date</Label>
                    <Input type="date" value={form.review_date} onChange={e => handleChange('review_date', e.target.value)} className="bg-slate-900 border-slate-700" />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-slate-200">Assessor / DPO</Label>
                    <Input value={form.assessor_dpo} onChange={e => handleChange('assessor_dpo', e.target.value)} placeholder="Name and role" className="bg-slate-900 border-slate-700" />
                  </div>
                </div>
              </>
            )}

            {/* ── Step 1: Transfer Overview ── */}
            {step === 1 && (
              <>
                <Field label="Description of Transfer" guidance="Briefly describe what data is being transferred and why" field="transfer_description" value={form.transfer_description} onChange={handleChange} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Data Exporter" guidance="Name, country, role (controller/processor)" field="data_exporter" value={form.data_exporter} onChange={handleChange} rows={2} />
                  <Field label="Data Importer" guidance="Name, country, role (controller/processor)" field="data_importer" value={form.data_importer} onChange={handleChange} rows={2} />
                </div>
                <InlineSelect label="Transfer Mechanism" field="transfer_mechanism" value={form.transfer_mechanism} onChange={handleChange} options={TRANSFER_MECHANISMS} />
                <Field label="Categories of Data Subjects" guidance="e.g., Employees, Customers, Policyholders" field="categories_data_subjects" value={form.categories_data_subjects} onChange={handleChange} rows={2} />
                <Field label="Categories of Personal Data" guidance="e.g., Name, contact details, financial records" field="categories_personal_data" value={form.categories_personal_data} onChange={handleChange} rows={2} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Special Category Data?</Label>
                    <Select value={form.special_category_data} onValueChange={v => handleChange('special_category_data', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes (specify below)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <InlineSelect label="Frequency of Transfer" field="frequency" value={form.frequency} onChange={handleChange} options={FREQUENCY_OPTS} />
                </div>
                {form.special_category_data === 'Yes' && (
                  <Field label="Special Category Data — Details" guidance="Specify the categories of special data included" field="special_category_detail" value={form.special_category_detail} onChange={handleChange} rows={2} />
                )}
                <Field label="Purpose of Transfer" guidance="e.g., Cloud hosting, payroll processing, claims handling" field="purpose_of_transfer" value={form.purpose_of_transfer} onChange={handleChange} rows={2} />
                <div className="space-y-1.5">
                  <Label className="text-slate-200">Retention Period</Label>
                  <Input value={form.retention_period} onChange={e => handleChange('retention_period', e.target.value)} placeholder="How long will data be held in the destination country?" className="bg-slate-900 border-slate-700" />
                </div>
              </>
            )}

            {/* ── Step 2: Destination Country Assessment ── */}
            {step === 2 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-slate-200">Destination Country <span className="text-rose-400">*</span></Label>
                    <Input value={form.destination_country} onChange={e => handleChange('destination_country', e.target.value)} placeholder="Country name" className="bg-slate-900 border-slate-700" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Adequacy Decision?</Label>
                    <Select value={form.adequacy_decision} onValueChange={v => handleChange('adequacy_decision', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes (reference below)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.adequacy_decision === 'Yes' && (
                    <div className="space-y-1.5">
                      <Label className="text-slate-200">Adequacy Decision Reference</Label>
                      <Input value={form.adequacy_decision_ref} onChange={e => handleChange('adequacy_decision_ref', e.target.value)} placeholder="e.g., EU–UK adequacy decision 2021" className="bg-slate-900 border-slate-700" />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Data Protection Law</Label>
                    <Input value={form.data_protection_law} onChange={e => handleChange('data_protection_law', e.target.value)} placeholder="Name of applicable law, if any" className="bg-slate-900 border-slate-700" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Independent Supervisory Authority</Label>
                    <Input value={form.supervisory_authority} onChange={e => handleChange('supervisory_authority', e.target.value)} placeholder="Name of authority (or 'None')" className="bg-slate-900 border-slate-700" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-200">Rule of Law Index Ranking</Label>
                    <Input value={form.rule_of_law_index} onChange={e => handleChange('rule_of_law_index', e.target.value)} placeholder="Rank / Score from World Justice Project" className="bg-slate-900 border-slate-700" />
                  </div>
                </div>

                {/* Risk Factors Table */}
                <div className="space-y-3">
                  <Label className="text-slate-200 text-sm font-semibold">Destination Country Risk Factors</Label>
                  <div className="rounded-lg border border-slate-800 overflow-hidden">
                    <div className="grid grid-cols-[1fr_auto] bg-slate-800/60 px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                      <span>Risk Factor</span>
                      <span className="w-28 text-center">Risk Level</span>
                    </div>
                    {RISK_FACTORS.map(rf => (
                      <div key={rf.key} className="border-t border-slate-800 p-4 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-200">{rf.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{rf.guidance}</p>
                          </div>
                          <div className="w-28 shrink-0">
                            <Select
                              value={form[`risk_${rf.key}_level`] || ''}
                              onValueChange={v => handleChange(`risk_${rf.key}_level`, v)}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-xs">
                                <SelectValue placeholder="Level" />
                              </SelectTrigger>
                              <SelectContent>
                                {RISK_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Textarea
                          value={form[`risk_${rf.key}_assessment`] || ''}
                          onChange={e => handleChange(`risk_${rf.key}_assessment`, e.target.value)}
                          placeholder="Assessment notes..."
                          rows={2}
                          className="bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-600 resize-none text-sm focus:border-violet-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Overall Country Risk */}
                <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-800/40 border border-slate-700">
                  <div className="flex-1">
                    <Label className="text-slate-200 font-semibold">Overall Country Risk Rating</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Combined assessment of the destination country's data protection landscape</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {form.overall_country_risk && <RiskBadge level={form.overall_country_risk} />}
                    <Select value={form.overall_country_risk} onValueChange={v => handleChange('overall_country_risk', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 w-32">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS_EXT.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* ── Step 3: Supplementary Measures ── */}
            {step === 3 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  Identify technical, organisational, and contractual measures in place or planned to mitigate identified risks.
                </div>
                <div className="rounded-lg border border-slate-800 overflow-hidden">
                  <div className="grid grid-cols-[1fr_auto] bg-slate-800/60 px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    <span>Measure</span>
                    <span className="w-32 text-center">Status</span>
                  </div>
                  {MEASURES.map(m => (
                    <div key={m.key} className="border-t border-slate-800 p-4 space-y-2">
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-medium text-slate-200">{m.label}</p>
                        <div className="w-32 shrink-0">
                          <Select
                            value={form[`measure_${m.key}_status`] || ''}
                            onValueChange={v => handleChange(`measure_${m.key}_status`, v)}
                          >
                            <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-xs">
                              <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                              {MEASURE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Input
                        value={form[`measure_${m.key}_desc`] || ''}
                        onChange={e => handleChange(`measure_${m.key}_desc`, e.target.value)}
                        placeholder={m.placeholder}
                        className="bg-slate-900 border-slate-700 text-sm text-slate-300"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── Step 4: Risk Assessment Summary ── */}
            {step === 4 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  Summarise the residual risk after considering the destination country assessment and supplementary measures.
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {/* Inherent Risk */}
                  <div className="p-4 rounded-lg border border-slate-700 bg-slate-900/60 space-y-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Inherent Risk</p>
                    <p className="text-xs text-slate-500">Before any measures</p>
                    {form.inherent_risk && <RiskBadge level={form.inherent_risk} />}
                    <Select value={form.inherent_risk} onValueChange={v => handleChange('inherent_risk', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-xs mt-2">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS_EXT.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Effectiveness */}
                  <div className="p-4 rounded-lg border border-slate-700 bg-slate-900/60 space-y-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Effectiveness of Measures</p>
                    <p className="text-xs text-slate-500">How well do supplementary measures mitigate risk?</p>
                    <Select value={form.measures_effectiveness} onValueChange={v => handleChange('measures_effectiveness', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-xs mt-2">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {EFFECTIVENESS_OPTS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Residual Risk */}
                  <div className="p-4 rounded-lg border border-slate-700 bg-slate-900/60 space-y-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Residual Risk</p>
                    <p className="text-xs text-slate-500">After supplementary measures</p>
                    {form.residual_risk && <RiskBadge level={form.residual_risk} />}
                    <Select value={form.residual_risk} onValueChange={v => handleChange('residual_risk', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 h-8 text-xs mt-2">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS_EXT.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Field
                  label="Narrative Assessment"
                  guidance="Brief narrative explaining the risk assessment conclusion, key factors considered, and rationale for the residual risk rating."
                  field="narrative_assessment"
                  value={form.narrative_assessment}
                  onChange={handleChange}
                  rows={5}
                />
              </>
            )}

            {/* ── Step 5: Transfer Decision ── */}
            {step === 5 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  Based on the assessment above, record the decision regarding this transfer.
                </div>

                {/* Decision buttons */}
                {!isReadOnly && (
                  <div className="space-y-3">
                    <Label className="text-slate-200 font-semibold">Transfer Decision</Label>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {DECISION_OPTS.map(d => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => handleChange('decision', d)}
                          className={cn(
                            "p-3 rounded-lg border-2 text-center text-sm font-medium transition-all",
                            form.decision === d ? DECISION_COLORS[d] : "border-slate-700 text-slate-400 hover:border-slate-600"
                          )}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {(form.decision === 'Approve with Conditions' || form.decision === 'Suspend') && (
                  <Field
                    label="Conditions / Actions Required"
                    guidance="List any conditions that must be met before transfer proceeds"
                    field="decision_conditions"
                    value={form.decision_conditions}
                    onChange={handleChange}
                    rows={3}
                  />
                )}

                <div className="space-y-1.5">
                  <Label className="text-slate-200">Next Review Date</Label>
                  <Input
                    type="date"
                    value={form.next_review_date}
                    onChange={e => handleChange('next_review_date', e.target.value)}
                    className="bg-slate-900 border-slate-700"
                  />
                </div>

                {!isReadOnly && form.decision && (
                  <div className="pt-4 border-t border-slate-800 flex justify-end">
                    <Button
                      className={cn(
                        "font-medium",
                        form.decision === 'Approve' ? "bg-emerald-600 hover:bg-emerald-700" :
                        form.decision === 'Approve with Conditions' ? "bg-amber-600 hover:bg-amber-700" :
                        form.decision === 'Suspend' ? "bg-orange-600 hover:bg-orange-700" :
                        "bg-rose-600 hover:bg-rose-700"
                      )}
                      onClick={() => {
                        const statusMap = {
                          'Approve': 'approved', 'Approve with Conditions': 'under_review',
                          'Suspend': 'suspended', 'Reject': 'rejected',
                        };
                        handleSave(statusMap[form.decision] || form.status);
                      }}
                      disabled={saving}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Finalise — {form.decision}
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* ── Step 6: Sign-Off ── */}
            {step === 6 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  Obtain sign-off from the relevant parties. This section documents approval of the TIA.
                </div>
                <div className="rounded-lg border border-slate-800 overflow-hidden">
                  <div className="grid grid-cols-3 bg-slate-800/60 px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    <span>Role</span><span>Name</span><span>Date</span>
                  </div>
                  {[
                    { role: 'Assessor / DPO',              nameField: 'signoff_assessor_name',    dateField: 'signoff_assessor_date' },
                    { role: 'Data Owner / Controller',      nameField: 'signoff_data_owner_name',  dateField: 'signoff_data_owner_date' },
                    { role: 'Approved By (Senior Mgmt)',    nameField: 'signoff_approved_by_name', dateField: 'signoff_approved_by_date' },
                  ].map(row => (
                    <div key={row.role} className="grid grid-cols-3 border-t border-slate-800 px-4 py-3 items-center gap-3">
                      <p className="text-sm text-slate-300 font-medium">{row.role}</p>
                      <Input
                        value={form[row.nameField] || ''}
                        onChange={e => handleChange(row.nameField, e.target.value)}
                        placeholder="Full name"
                        className="bg-slate-900 border-slate-700 text-sm h-8"
                      />
                      <Input
                        type="date"
                        value={form[row.dateField] || ''}
                        onChange={e => handleChange(row.dateField, e.target.value)}
                        className="bg-slate-900 border-slate-700 text-sm h-8"
                      />
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-slate-400">
                  <strong className="text-amber-400">Document Control:</strong> This TIA should be reviewed at least annually, or whenever there is a material change in the transfer circumstances, the legal framework of the destination country, or the supplementary measures in place.
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ── Navigation ── */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" /> Previous
          </Button>
          <div className="flex gap-2">
            {!isReadOnly && (
              <Button variant="outline" onClick={() => handleSave()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Draft
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
              >
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
